import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import adminProductsService, {
  type AvailableVehicle,
  type Market,
} from '../../services/adminProducts.service';
import {
  MARKETS,
  productProblem,
  toCreateBody,
  toggleMarket,
  type ProductForm,
} from './adminProducts.helpers';

const EMPTY_FORM: ProductForm = { fundsProductId: '', markets: ['MX'], description: '', riskLevel: '' };

/**
 * Super admin: create a product (#277). One super admin names a funds vehicle and creates its
 * product with an authentication code. The product is created NOT offered; it is offered only
 * when it meets every requirement (a bank account in force, books ready, a published contract
 * template, an offering document, a commitment configuration) - see Config health - and only in
 * the countries that are switched on. A product's fund and kind never change afterwards.
 */
export default function AdminProducts(): JSX.Element {
  const [vehicles, setVehicles] = useState<AvailableVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setVehicles(await adminProductsService.availableVehicles());
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not load the products.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Create a product"
        subtitle="A product is the portal side of one funds vehicle. It starts not offered, and becomes offered by itself once it meets every requirement."
      />
      {err ? <div style={errBox}>{err}</div> : null}
      {flash ? <div style={okBox}>{flash}</div> : null}
      <div style={{ display: 'grid', gap: 16, maxWidth: 820 }}>
        <CreateForm
          vehicles={vehicles}
          loading={loading}
          onCreated={async (message) => {
            setFlash(message);
            await reload();
          }}
        />
      </div>
    </div>
  );
}

/** What funds fixed the chosen vehicle as (#383): shown, never editable here. A vehicle without it
 *  cannot become a product. */
function VehicleRecord({ vehicle }: Readonly<{ vehicle: AvailableVehicle | undefined }>): JSX.Element | null {
  if (vehicle === undefined) return null;
  if (vehicle.product_type === null || vehicle.exit_path === null || vehicle.price_basis === null) {
    return (
      <p style={noteText}>
        Funds did not state what this vehicle is, so a product cannot be created over it yet.
      </p>
    );
  }
  return (
    <p style={noteText}>
      {'Fixed by funds when the vehicle was created:'}{' '}
      <span translate="no">{`${vehicle.product_type} · ${vehicle.exit_path} · ${vehicle.price_basis}`}</span>
    </p>
  );
}

function CreateForm({
  vehicles,
  loading,
  onCreated,
}: Readonly<{
  vehicles: AvailableVehicle[];
  loading: boolean;
  onCreated: (message: string) => Promise<void>;
}>): JSX.Element {
  const [form, setForm] = useState<ProductForm>(EMPTY_FORM);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const problem = productProblem(form);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const made = await adminProductsService.create(toCreateBody(form, code));
      setForm(EMPTY_FORM);
      setCode('');
      await onCreated(
        made.is_active
          ? 'Product created and offered.'
          : 'Product created. It is not offered yet: Config health lists what it still needs.',
      );
    } catch (e) {
      setErr(stepUpErrorMessage(e, 'Could not create the product.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={card}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Create a product</h2>
      {err ? <div style={errBox}>{err}</div> : null}
      <label style={labelStyle}>
        <span>Funds vehicle</span>
        <select
          value={form.fundsProductId}
          disabled={busy || loading}
          onChange={(e) => setForm({ ...form, fundsProductId: e.target.value })}
          style={inputStyle}
        >
          <option value="">Choose a vehicle…</option>
          {vehicles.map((v) => (
            <option key={v.funds_product_id} value={v.funds_product_id} translate="no">
              {`${v.name} · ${v.currency_code}`}
            </option>
          ))}
        </select>
      </label>
      <VehicleRecord vehicle={vehicles.find((v) => v.funds_product_id === form.fundsProductId)} />
      {vehicles.length === 0 && !loading ? (
        <p style={noteText}>Every funds vehicle already has a product.</p>
      ) : null}
      <fieldset style={{ border: 'none', padding: 0, margin: '12px 0 0' }}>
        <legend style={labelStyle}>Markets where it is offered</legend>
        <div style={{ display: 'flex', gap: 16 }}>
          {MARKETS.map((m: Market) => (
            <label key={m} style={{ fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.markets.includes(m)}
                disabled={busy}
                onChange={() => setForm({ ...form, markets: toggleMarket(form.markets, m) })}
              />{' '}
              <span translate="no">{m}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label style={{ ...labelStyle, marginTop: 12 }}>
        <span>Risk level (1 to 5, optional)</span>
        <input
          type="number"
          min={1}
          max={5}
          value={form.riskLevel}
          disabled={busy}
          onChange={(e) => setForm({ ...form, riskLevel: e.target.value })}
          style={{ ...inputStyle, width: 90 }}
        />
      </label>
      <label style={{ ...labelStyle, marginTop: 12 }}>
        <span>What clients are told it is (shown on the product page)</span>
        <textarea
          value={form.description}
          rows={4}
          maxLength={2000}
          disabled={busy}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          style={inputStyle}
        />
      </label>
      {problem && form.description.length > 0 ? <p style={noteText}>{problem}</p> : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
        <label style={labelStyle}>
          <span>Authentication code</span>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(cleanMfaInput(e.target.value))}
            style={{ ...inputStyle, width: 140 }}
          />
        </label>
        <button
          type="button"
          disabled={busy || problem !== null || !isMfaCode(code)}
          onClick={() => void submit()}
          style={btnPrimary}
        >
          {busy ? 'Saving…' : 'Create product'}
        </button>
      </div>
    </section>
  );
}

const card: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
};
const noteText: React.CSSProperties = { fontSize: 12, color: tokens.textMuted, margin: '4px 0 0' };
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const errBox: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
};
const okBox: React.CSSProperties = {
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
};
